import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import { App } from './App.tsx';
import { SessionProvider, Splash } from './auth/session.tsx';
import { fetchAuthConfig } from './api/client.ts';
import type { AuthConfig } from './api/types.ts';

const queryClient = new QueryClient();

/**
 * The app asks the server how to sign in before it renders anything.
 *
 * This is the one call made without a token, and it is what keeps the dev
 * sign-in path out of Azure: a build-time flag would be baked into the bundle
 * and travel with the artifact.
 */
function Boot() {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchAuthConfig<AuthConfig>().then(setConfig).catch((e: Error) => setError(e.message));
  }, []);

  if (error) return <Splash>{error}</Splash>;
  if (!config) return <Splash>Connecting…</Splash>;

  return (
    <SessionProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </SessionProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Boot />
  </StrictMode>,
);
